/**
 * ConsistencyMatrix — claims × AI-platforms grid showing where a message holds.
 * Accessible data table (role-friendly) + colour legend. Zero hardcoded labels.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { Check, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface ConsistencyMatrixProps {
  namespace?: string;
  className?: string;
}

const PLATFORMS = ['p1', 'p2', 'p3', 'p4'] as const;
const CLAIMS = ['c1', 'c2', 'c3'] as const;

// Illustrative consistency pattern (true = consistent on that platform).
const PATTERN: Record<(typeof CLAIMS)[number], boolean[]> = {
  c1: [true, true, true, true],
  c2: [true, true, false, true],
  c3: [true, false, true, true],
};

export function ConsistencyMatrix({ namespace = 'marketing.diagrams.consistencyMatrix', className }: ConsistencyMatrixProps) {
  const t = useTranslations(namespace);
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div role="img" aria-label={t('title')} className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <svg width="0" height="0" aria-hidden="true" className="absolute">
          <title>{t('title')}</title>
        </svg>
        {/* Horizontally-scrollable on narrow viewports → keyboard-reachable
            (axe scrollable-region-focusable); named by the parent role="img". */}
        <div className="mt-5 overflow-x-auto" tabIndex={0}>
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">{t('title')}</caption>
            <thead>
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium text-white/50">
                  <span className="sr-only">{t('title')}</span>
                </th>
                {PLATFORMS.map((p) => (
                  <th key={p} scope="col" className="px-3 py-2 text-center font-medium text-cyan-300">
                    {t(`platforms.${p}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CLAIMS.map((claim) => (
                <tr key={claim} className="border-t border-white/5">
                  <th scope="row" className="px-3 py-2.5 text-left font-normal text-white/80">
                    {t(`claims.${claim}`)}
                  </th>
                  {PATTERN[claim].map((on, ci) => (
                    <td key={ci} className="px-3 py-2.5 text-center">
                      {on ? (
                        <Check size={18} className="mx-auto text-emerald-300" aria-label={t('legendOn')} />
                      ) : (
                        <X size={18} className="mx-auto text-amber-400" aria-label={t('legendOff')} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-white/60">
          <span className="flex items-center gap-1.5">
            <Check size={14} aria-hidden="true" className="text-emerald-300" /> {t('legendOn')}
          </span>
          <span className="flex items-center gap-1.5">
            <X size={14} aria-hidden="true" className="text-amber-400" /> {t('legendOff')}
          </span>
        </div>
      </div>
    </MotionReveal>
  );
}
